import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import { BreadcrumbLabels } from './breadcrumb-labels';

const ROUTE_LABELS: Record<string, string> = {
  help: 'Aide & Documentation',
  'guided-tours': 'Aide & Documentation',
  dashboard: 'Tableau de bord',
  analytics: 'Analytique',
  patients: 'Patients',
  appointments: 'Rendez-vous',
  consultations: 'Consultations',
  prescriptions: 'Prescriptions',
  laboratory: 'Laboratoire',
  radiology: 'Imagerie médicale',
  tests: 'Tests',
  hospitalization: 'Hospitalisations',
  beds: 'Lits',
  surgeries: 'Chirurgies',
  'operating-rooms': 'Bloc opératoire',
  maternity: 'Maternité',
  vaccinations: 'Vaccinations',
  'blood-bank': 'Banque de sang',
  pharmacy: 'Pharmacie',
  'pharmacy-pos': 'Caisse POS',
  'pharmacy-sales': 'Ventes',
  'inventory-items': 'Articles',
  'stock-movements': 'Mouvements stock',
  'stock-alerts': 'Alertes stock',
  'warehouse-items': 'Entrepôt',
  ambulances: 'Ambulances',
  'ambulance-dispatch': 'Dispatch',
  'ambulance-missions': 'Missions',
  'emergency-calls': 'Urgences',
  billing: 'Facturation',
  insurances: 'Assurances',
  'insurance-pre-auths': 'Pré-autorisations',
  'insurance-claims': 'Remboursements',
  doctors: 'Médecins',
  staff: 'Personnel',
  shifts: 'Plannings',
  attendances: 'Présences',
  'leave-requests': 'Congés',
  roles: 'Rôles & Permissions',
  organization: 'Organisation',
  audit: "Journal d'audit",
  'audit-logs': "Journal d'audit",
  reports: 'Rapports',
  usage: 'Consommation',
  alerts: 'Alertes',
  'api-keys': 'Clés API',
  webhooks: 'Webhooks',
  backups: 'Sauvegardes',
  settings: 'Paramètres',
  new: 'Nouveau',
  edit: 'Modifier',
  detail: 'Détail',
  results: 'Résultats',
  pregnancies: 'Grossesses',
  'antenatal-visits': 'Visites prénatales',
  'labor-deliveries': 'Accouchements',
  newborns: 'Nouveau-nés',
  'patient-queue': "File d'attente",
  'chronic-diseases': 'Maladies chroniques',
  'pre-registrations': 'Pré-enregistrements',
  referrals: 'Référencements',
  'patient-records': 'Dossiers patients',
  'e-carnets': 'E-Carnets',
  'medical-leave-forms': 'Arrêts maladie',
  teleconsultations: 'Téléconsultations',
  'vaccination-certificates': 'Certificats vaccination',
  'blood-donations': 'Dons de sang',
  'pharmacy-purchase-orders': 'Commandes pharma',
  'pharmacy-suppliers': 'Fournisseurs pharma',
  inventory: 'Inventaire',
  'stock-transfers': 'Transferts stock',
  'ambulance-equipments': 'Équipements ambulances',
  'ambulance-maintenances': 'Maintenance ambulances',
  'ambulance-personnels': 'Personnels ambulances',
  'credit-notes': 'Avoirs',
  quotes: 'Devis',
  'payment-plans': 'Plans de paiement',
  transactions: 'Transactions',
  accounting: 'Comptabilité',
  'journal-entries': 'Journal comptable',
  services: 'Services',
  complaints: 'Réclamations',
  forms: 'Formulaires',
  'form-responses': 'Réponses formulaires',
  'adverse-events': 'Événements indésirables',
  'lab-providers': 'Fournisseurs labo',
  'lab-reagents': 'Réactifs labo',
  'lab-results': 'Résultats labo',
  'medical-documents': 'Documents médicaux',
  'waiting-rooms': "Salles d'attente",
  'patient-portal': 'Portail patient',
  signup: 'Inscription',
  login: 'Connexion',
  auth: 'Authentification',
  'signature-config': 'Configuration signature',
  'surgical-equipments': 'Équipements chirurgicaux',
  'consultation-rapide': 'Consultation rapide',
  // Ajouts audit breadcrumb — 17 routes orphelines détectées dans app.routes.ts
  'ambulance-analytics': 'Analytics ambulance',
  'custom-forms': 'Formulaires personnalisés',
  'debt-recovery': 'Recouvrement',
  departments: 'Départements',
  'icd11-terminology': 'Terminologie CIM-11',
  'medical-records': 'Dossiers médicaux',
  'nursing-care': 'Soins infirmiers',
  payroll: 'Paie',
  'pharmacy-products': 'Produits pharmacie',
  queue: "File d'attente",
  'rh-dashboard': 'Tableau RH',
  'secure-messages': 'Messages sécurisés',
  'super-admin': 'Super Admin',
};

@Component({
  selector: 'app-breadcrumb',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, BreadcrumbModule],
  template: `
    @if (items().length > 1) {
      <p-breadcrumb
        [model]="items()"
        [home]="home"
        homeAriaLabel="Accueil"
        styleClass="!bg-transparent !border-none !px-0 !py-0 !text-xs"
      />
    }
  `,
})
export class BreadcrumbComponent implements OnInit {
  private router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };
  private readonly dynamicLabels = inject(BreadcrumbLabels);
  private readonly segments = signal<string[]>([]);

  /** Segments de l'URL courante ; un segment dynamique prend le libellé publié par l'écran de détail. */
  readonly items = computed<MenuItem[]>(() => {
    const labels = this.dynamicLabels.all();
    const segments = this.segments();
    return segments.map((seg, i) => ({
      label: labels[seg] || ROUTE_LABELS[seg] || this.formatSegment(seg),
      routerLink: '/' + segments.slice(0, i + 1).join('/'),
    }));
  });

  ngOnInit() {
    this.buildBreadcrumb();
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.buildBreadcrumb());
  }

  private buildBreadcrumb() {
    const url = this.router.url.replace(/^\//, '').split(/[?#]/)[0];
    this.segments.set(url.split('/').filter(Boolean));
  }

  private formatSegment(seg: string): string {
    // For raw IDs (24-hex MongoDB hash), show a truncated hash with ellipsis
    // rather than the noisy uppercased ID. Same for UUIDs.
    if (/^[a-f0-9]{24}$/i.test(seg)) return seg.slice(0, 8) + '…';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg))
      return seg.slice(0, 8) + '…';
    return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
